import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { type WorkspaceAgentRunRecord } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { getLatestConfirmedUserMessageId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getLatestConfirmedUserMessageId';

export type WorkspaceAgentRunSegmentBounds = {
  nextUserSequence: number;
  userMessage: WorkspaceAgentMessage;
};

/** Sequence after which the next user message starts, or infinity when this turn has no successor. */
export const getWorkspaceAgentRunSegmentBounds = (
  messages: readonly WorkspaceAgentMessage[],
  userMessageId: string,
): WorkspaceAgentRunSegmentBounds | undefined => {
  const userMessage = messages.find((message) => message.id === userMessageId);

  if (!userMessage) {
    return undefined;
  }

  const nextUserSequence =
    messages.find((message) => message.role === 'user' && message.sequence > userMessage.sequence)?.sequence ??
    Number.POSITIVE_INFINITY;

  return { nextUserSequence, userMessage };
};

export const isSequenceInWorkspaceAgentRunSegment = (
  sequence: number,
  bounds: WorkspaceAgentRunSegmentBounds,
  afterSequence: number = bounds.userMessage.sequence,
): boolean => sequence > afterSequence && sequence < bounds.nextUserSequence;

/** Messages after a user turn and before the next user message — one agent run's tool calls/results. */
export const getWorkspaceAgentRunSegmentMessages = (
  messages: readonly WorkspaceAgentMessage[],
  userMessageId: string,
): WorkspaceAgentMessage[] => {
  const bounds = getWorkspaceAgentRunSegmentBounds(messages, userMessageId);

  if (!bounds) {
    return [];
  }

  return messages.filter((message) => isSequenceInWorkspaceAgentRunSegment(message.sequence, bounds));
};

export type GetWorkspaceAgentFinishedRunUserMessageIdParams = {
  messages: readonly WorkspaceAgentMessage[];
  runId: string;
  runsById: Record<string, WorkspaceAgentRunRecord>;
};

/**
 * The finished run's triggering user row, even when it is still pending. Latest confirmed is only
 * a fallback when that row is missing, so a later send after cancel is not scanned instead.
 */
export const getWorkspaceAgentFinishedRunUserMessageId = ({
  messages,
  runId,
  runsById,
}: GetWorkspaceAgentFinishedRunUserMessageIdParams): string | null => {
  const triggeringUserMessageId = runsById[runId]?.triggeringUserMessageId;

  if (triggeringUserMessageId && messages.some((message) => message.id === triggeringUserMessageId)) {
    return triggeringUserMessageId;
  }

  return getLatestConfirmedUserMessageId(messages);
};
